import './DtcCommerceMmmFilters.scss';
import { MdOutlineAutoAwesome } from 'react-icons/md';
import { DatePicker } from '@Components/DatePicker';
import { DateRange as TinuitiDateRange } from '@Components/DatePicker/date-picker-types';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Appearance, Design, Placement, Size } from '@Components/designConstants';
import { useDtcCommerceMmmData } from '../context/DtcCommerceMmmDataContext';
import { ATTRIBUTION_OPTIONS, HIERARCHY_OPTIONS } from '../dtc-commerce-mmm-constants';
import {
  AttributionType,
  DateRange,
  DateRangePresetConst,
  HierarchyLevel
} from '../dtc-commerce-mmm-types';

interface DtcCommerceMmmFiltersProps {
  hierarchyLevel: HierarchyLevel;
  onHierarchyChange: (level: HierarchyLevel) => void;
  agentOpen: boolean;
  onToggleAgent: () => void;
}

export const DtcCommerceMmmFilters = ({
  hierarchyLevel,
  onHierarchyChange,
  agentOpen,
  onToggleAgent
}: DtcCommerceMmmFiltersProps) => {
  const { data, attributionType, setAttributionType, dateRange, setDateRange } =
    useDtcCommerceMmmData();

  const handleCalendarChange = (newRange: TinuitiDateRange) => {
    const next: DateRange = {
      preset: DateRangePresetConst.CUSTOM,
      startDate: newRange.start,
      endDate: newRange.end,
      label: 'Custom'
    };
    setDateRange(next);
  };

  const attributionOptions = ATTRIBUTION_OPTIONS.map(o => ({ key: o.key, label: o.label }));
  const hierarchyDropdownOptions = HIERARCHY_OPTIONS.map(o => ({ label: o.label, value: o.value }));

  const calendarRange: TinuitiDateRange = {
    start: dateRange.startDate,
    end: dateRange.endDate
  };

  // Actualized/Direct toggle hidden — keep all supporting plumbing in place so it can be
  // restored by re-rendering <SegmentedControlButton size={Size.LG}> with the same props below.
  const showAttributionToggle = false;

  return (
    <div className='dtc-commerce-mmm-filters'>
      <Dropdown<HierarchyLevel>
        size={Size.LG}
        overlayPlacement={Placement.RIGHT}
        label='Group by'
        options={hierarchyDropdownOptions}
        value={hierarchyLevel}
        onChange={onHierarchyChange}
      />
      {showAttributionToggle && (
        <SegmentedControlButton<AttributionType>
          size={Size.LG}
          options={attributionOptions}
          selectedOption={attributionType}
          onChange={setAttributionType}
        />
      )}
      <DatePicker
        range={calendarRange}
        onChange={handleCalendarChange}
        fullWeeksOnly
        minDate={data?.dataStartDate}
        maxDate={data?.dataEndDate}
        isOutsideRange={date =>
          Boolean(data) && (date < (data?.dataStartDate ?? '') || date > (data?.dataEndDate ?? ''))
        }
        isDayBlocked={date => new Date(`${date}T00:00:00`).getDay() !== 1}
        design={Design.BRAND}
      />
      <IconButton
        appearance={Appearance.EMPTY}
        size={Size.LG}
        onClick={onToggleAgent}
        aria-label={agentOpen ? 'Close MMM Agent' : 'Open MMM Agent'}
        aria-pressed={agentOpen}
      >
        <MdOutlineAutoAwesome />
      </IconButton>
    </div>
  );
};
